/**
 * M8.2: Einladungs-Token ueber den Auth-Roundtrip hinweg merken.
 *
 * Der Token wird auf der Einladungs-Landingpage (/einladung/:token) gesichert,
 * damit Signup-/Login-/Verify-Flows ihn auch nach Redirects wiederfinden.
 * Wie lib/onboarding.ts: localStorage ist browser-lokal — ein Browser-Wechsel
 * verliert den gemerkten Token (der Link in der E-Mail bleibt trotzdem gueltig).
 */
import { z } from 'zod'
import { apiRequest } from '@/lib/api-client'

const KEY = 'mindkeep:invite-token'
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

interface StoredInviteToken {
  token: string
  storedAt: number
}

export function saveInviteToken(token: string): void {
  if (!token || typeof window === 'undefined') return
  try {
    const entry: StoredInviteToken = { token, storedAt: Date.now() }
    window.localStorage.setItem(KEY, JSON.stringify(entry))
  } catch {
    // Storage voll/blockiert — der ?invite=-URL-Param traegt den Token weiter.
  }
}

/** Gemerkten Token lesen; null, wenn nichts gespeichert oder aelter als 7 Tage. */
export function readInviteToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return null
    const entry = JSON.parse(raw) as Partial<StoredInviteToken>
    if (typeof entry.token !== 'string' || typeof entry.storedAt !== 'number') return null
    if (Date.now() - entry.storedAt > MAX_AGE_MS) {
      window.localStorage.removeItem(KEY)
      return null
    }
    return entry.token
  } catch {
    return null
  }
}

export function clearInviteToken(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    // Nichts aufzuraeumen.
  }
}

const publicInviteSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: z.enum(['owner', 'admin', 'member', 'viewer']),
  workspace_id: z.string(),
  workspace_name: z.string(),
  expires_at: z.string(),
})

export type PublicInvite = z.infer<typeof publicInviteSchema>

/** Oeffentliche Einladungs-Infos (funktioniert ohne Session). */
export async function fetchPublicInvite(token: string): Promise<PublicInvite> {
  return apiRequest({ path: `/workspace/invites/${token}`, schema: publicInviteSchema })
}

const acceptInviteResponseSchema = z.object({
  status: z.enum(['accepted', 'already_member']),
  workspace_id: z.string(),
})

export type AcceptInviteResult = z.infer<typeof acceptInviteResponseSchema>

/** Einladung annehmen (erfordert Session; E-Mail muss zur Einladung passen). */
export async function acceptInvite(token: string): Promise<AcceptInviteResult> {
  return apiRequest({
    method: 'POST',
    path: `/workspace/invites/${token}/accept`,
    schema: acceptInviteResponseSchema,
  })
}

/** Einheitlicher Toast-Text fuer beide Accept-Ergebnisse. */
export function acceptInviteToast(status: AcceptInviteResult['status']): string {
  return status === 'already_member'
    ? 'Du bist bereits Mitglied dieses Workspace.'
    : 'Einladung angenommen.'
}
